import { supabase } from './supabaseClient';

/**
 * Calculate compatibility matches for a user.
 * Score: 0-100 based on interests, industry, stage, geography,
 * complementary skills, and shared pensamientos tags.
 */
export async function calcularMatches(userId) {
  // 1. My profile
  const { data: yo } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (!yo) return [];

  // 2. Other users with completed onboarding
  const { data: otros } = await supabase
    .from('profiles')
    .select('*')
    .neq('id', userId)
    .eq('onboarding_done', true);

  if (!otros || otros.length === 0) return [];

  // Parse comma-separated fields into arrays
  const parseList = (str) => (str || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

  const misSkills = parseList(yo.skill);
  const misNeeds = parseList(yo.pain_point);

  // 3. Score each user
  const matches = otros.map(otro => {
    let score = 0;
    const razones = [];

    // INTERESES (25 pts) — stored as onboarding checkbox IDs
    // Not stored in profiles directly, skip if not available

    // INDUSTRIA (8 pts)
    if (yo.business_type && otro.business_type && yo.business_type === otro.business_type) {
      score += 8;
      razones.push({ emoji: '🏷️', text: 'Misma industria' });
    }

    // ETAPA (7 pts)
    if (yo.business_stage && otro.business_stage && yo.business_stage === otro.business_stage) {
      score += 7;
      razones.push({ emoji: '📊', text: 'Misma etapa' });
    }

    // GEOGRAFÍA (15 pts: 5 país + 10 ciudad/región)
    if (yo.country_id && otro.country_id && yo.country_id === otro.country_id) {
      score += 5;
      if (yo.region_id && otro.region_id && yo.region_id === otro.region_id) {
        score += 5;
        razones.push({ emoji: '📍', text: 'Misma region' });
      }
      if (yo.city && otro.city && yo.city.toLowerCase() === otro.city.toLowerCase()) {
        score += 5;
        razones.push({ emoji: '🏘️', text: 'Misma ciudad' });
      }
    }

    // HABILIDADES COMPLEMENTARIAS (25 pts)
    const otroSkills = parseList(otro.skill);
    const otroNeeds = parseList(otro.pain_point);

    // Mis skills cubren sus necesidades
    const yoCubroSusNeeds = otroNeeds.filter(n => misSkills.includes(n));
    // Sus skills cubren mis necesidades
    const elCubreMisNeeds = misNeeds.filter(n => otroSkills.includes(n));

    const complementPts = Math.min(25, (yoCubroSusNeeds.length + elCubreMisNeeds.length) * 5);
    score += complementPts;
    if (yoCubroSusNeeds.length > 0) {
      razones.push({ emoji: '🤝', text: `Tu sabes lo que ${otro.full_name?.split(' ')[0] || 'el/ella'} necesita` });
    }
    if (elCubreMisNeeds.length > 0) {
      razones.push({ emoji: '💡', text: `Sabe lo que tu necesitas` });
    }

    // SKILLS SIMILARES (10 pts) — tienen habilidades en comun
    const sharedSkills = misSkills.filter(s => otroSkills.includes(s));
    if (sharedSkills.length > 0) {
      score += Math.min(10, sharedSkills.length * 3);
      razones.push({ emoji: '⚡', text: `${sharedSkills.length} habilidad${sharedSkills.length > 1 ? 'es' : ''} en comun` });
    }

    // MODELO DE NEGOCIO (5 pts)
    if (yo.sales_type && otro.sales_type && yo.sales_type === otro.sales_type) {
      score += 5;
      razones.push({ emoji: '📦', text: 'Mismo modelo de negocio' });
    }

    // IDENTIDAD T/D (5 pts)
    if (yo.identidad && otro.identidad && yo.identidad === otro.identidad) {
      score += 5;
      razones.push({ emoji: '🧠', text: yo.identidad === 'thinker' ? 'Ambos Thinkers' : 'Ambos Doers' });
    }

    return {
      profile: otro,
      score: Math.min(100, score),
      razones,
    };
  });

  // 4. Filter and sort
  return matches
    .filter(m => m.score > 10)
    .sort((a, b) => b.score - a.score);
}
